import { useEffect, useRef } from 'react';
import './RecentWork.css';
import install1 from '../../assets/figma_install_1.jpg';
import install2 from '../../assets/figma_install_2.jpg';
import install3 from '../../assets/figma_install_3.jpg';
import imgDoncasterGrowatt  from '../../assets/rw_doncaster_growatt.png';
import imgHamptonGrowatt    from '../../assets/rw_hampton_growatt.png';
import imgHamptonGrowatt2   from '../../assets/rw_hampton_growatt2.png';
import imgHawthornAlpha     from '../../assets/rw_hawthorn_alpha.png';
import imgNarreSolax        from '../../assets/rw_narre_solax.png';
import imgDromanaSolax      from '../../assets/rw_dromana_solax.png';
import imgSouthYarraAlpha   from '../../assets/rw_southyarra_alpha.png';
import imgKnoxfieldAlpha    from '../../assets/rw_knoxfield_alpha.png';

const installations = [
  { image: install1,            location: 'Doncaster, VIC',          title: 'Solar + Battery System (AlphaESS - G3 - S5)' },
  { image: install2,            location: 'Point Cook, VIC',          title: 'Battery Upgrade (Goodwe ESA)' },
  { image: install3,            location: 'Glen Waverley, VIC',       title: 'Solar Installation + Hybrid Inverter (AlphaESS)' },
  { image: imgDoncasterGrowatt, location: 'Doncaster, VIC',          title: 'Battery system (Growatt ALP)' },
  { image: imgHawthornAlpha,    location: 'Hawthorn, VIC',            title: 'Battery system (AlphaESS)' },
  { image: imgNarreSolax,       location: 'Narre Warren South, VIC',  title: 'Battery System (SOLAX HS51)' },
  { image: imgDromanaSolax,     location: 'Dromana, VIC',             title: 'Battery System (SOLAX HS51)' },
  { image: imgSouthYarraAlpha,  location: 'South Yarra, VIC',         title: 'Battery system (AlphaESS)' },
  { image: imgHamptonGrowatt,   location: 'Hampton Park, VIC',        title: 'Battery system (Growatt ALP)' },
  { image: imgHamptonGrowatt2,  location: 'Hampton Park, VIC',        title: 'Battery system (Growatt ALP)' },
  { image: imgKnoxfieldAlpha,   location: 'Knoxfield, VIC',           title: 'Battery system (AlphaESS)' },
];

const LOOP_PX   = 11 * (420 + 24); // 4884 — total pixel distance of one loop
const PER_CARD  = 30;               // keep in sync with --per-card in CSS
const TOTAL_S   = 11 * PER_CARD;   // 330

const wrapX = (x) => {
  const m = x % LOOP_PX;
  return m > 0 ? m - LOOP_PX : m;
};

export default function RecentWork() {
  const trackRef  = useRef(null);
  const marqueeRef = useRef(null);

  useEffect(() => {
    const track   = trackRef.current;
    const marquee = marqueeRef.current;
    if (!track || !marquee) return;

    let dragging     = false;
    let dragStartX   = 0;
    let dragStartPos = 0;
    let manualPos    = 0;   // position while user is in control
    let isManual     = false;
    let wheelTimer   = null;

    // Read the current translateX from the running CSS animation.
    const getAnimatedX = () =>
      new DOMMatrix(window.getComputedStyle(track).transform).m41;

    // Hand control back to the CSS animation, starting from pos.
    const resumeFrom = (pos) => {
      const n       = wrapX(pos);
      const elapsed = (n / -LOOP_PX) * TOTAL_S;
      isManual = false;
      track.style.animation      = 'none';
      void track.offsetWidth;                  // flush so delay is re-applied
      track.style.animation      = `rw-scroll calc(11 * var(--per-card)) linear infinite`;
      track.style.animationDelay = `-${elapsed}s`;
      track.style.removeProperty('transform');
      track.style.removeProperty('cursor');
    };

    // Freeze animation and move to pos.
    const freezeAt = (pos) => {
      if (!isManual) {
        isManual  = true;
        manualPos = getAnimatedX();
        track.style.animation = 'none';
      }
      manualPos = wrapX(pos);
      track.style.transform = `translateX(${manualPos}px)`;
    };

    // ── Wheel / trackpad scroll ──────────────────────────────────────────────
    const onWheel = (e) => {
      e.preventDefault();
      // normalise pixel delta (deltaMode 1 = lines, mode 2 = pages)
      const raw   = e.deltaMode === 1 ? e.deltaY * 20 : e.deltaMode === 2 ? e.deltaY * 300 : e.deltaY;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : raw;

      freezeAt((isManual ? manualPos : getAnimatedX()) - delta);

      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => resumeFrom(manualPos), 1500);
    };

    // ── Mouse / touch drag ───────────────────────────────────────────────────
    const onDown = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      clearTimeout(wheelTimer);
      dragStartPos = isManual ? manualPos : getAnimatedX();
      dragStartX   = clientX;
      dragging     = true;
      freezeAt(dragStartPos);
      track.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const onMove = (e) => {
      if (!dragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      freezeAt(dragStartPos + (clientX - dragStartX));
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      resumeFrom(manualPos);
    };

    // Wheel on the marquee wrapper so it fires even at the edges.
    marquee.addEventListener('wheel',      onWheel, { passive: false });
    track.addEventListener('mousedown',    onDown);
    track.addEventListener('touchstart',   onDown,  { passive: false });
    window.addEventListener('mousemove',   onMove);
    window.addEventListener('mouseup',     onUp);
    window.addEventListener('touchmove',   onMove,  { passive: true });
    window.addEventListener('touchend',    onUp);

    return () => {
      marquee.removeEventListener('wheel',    onWheel);
      track.removeEventListener('mousedown',  onDown);
      track.removeEventListener('touchstart', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onUp);
      clearTimeout(wheelTimer);
    };
  }, []);

  return (
    <section className="recent-work" id="our-works">
      <div className="recent-inner">
        <h2 className="recent-title">Real installations across Melbourne</h2>
        <p className="recent-subtitle">
          Recent solar and battery systems installed for homeowners across Melbourne,
          tailored to real homes and real energy needs
        </p>

        <div className="recent-marquee" ref={marqueeRef}>
          <div className="recent-track" ref={trackRef}>
            {[...installations, ...installations].map((item, i) => (
              <div key={i} className="recent-card">
                <img src={item.image} alt={item.title} className="recent-card-img" />
                <div className="recent-card-overlay">
                  <span className="recent-card-location">{item.location}</span>
                  <h3 className="recent-card-title">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
